/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Button, Icon, Grid, Segment, Modal } from 'semantic-ui-react';
import { OPPORTUNITY_STATUS } from '../constants/opportunityStatuses';
import { cancellationCategoryLabel } from './OpportunityCancelModal';
import Timestamp from './Timestamp';

class CancelRequestDetail extends Component {
  static propTypes = {
    data: PropTypes.shape({
      id: PropTypes.string.isRequired,
      sr_status: PropTypes.string,
      sr_prev_status: PropTypes.string,
      primary_customer: PropTypes.shape({
        name: PropTypes.string,
      }),
      sr_cancellation_reason: PropTypes.string,
      sr_cancellation_type: PropTypes.string,
      sr_cancellation_at: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    }).isRequired,
    t: PropTypes.func.isRequired,
    hasAccess: PropTypes.bool,
    onUpdateOpportunity: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      isConfirmOpen: false,
      confirmType: null,
      isSubmitting: false,
    };
  }

  getBannerClassName = (status) => {
    if (this.isCancelled(status)) {
      return 'cancel-container-approved';
    }
    return 'cancel-container-received';
  }

  // CPI-1226: both terminal cancellations read as "approved"; only the CRM's grouping differs.
  isCancelled = status => (
    status === OPPORTUNITY_STATUS.CANCELLED || status === OPPORTUNITY_STATUS.CANCELLED_ERROR
  )

  handleOpenConfirm = (type) => {
    this.setState({
      isConfirmOpen: true,
      confirmType: type,
    });
  }

  handleCloseConfirm = () => {
    this.setState({ isConfirmOpen: false, confirmType: null });
  }

  handleConfirmAction = async () => {
    const { onUpdateOpportunity, data } = this.props;
    const { confirmType } = this.state;

    if (onUpdateOpportunity && confirmType && data && data.sr_prev_status) {
      let finalStatus = confirmType;
      const isUndo = confirmType === 'UNDO_CANCELLATION';
      this.setState({ isSubmitting: true });

      if (isUndo) {
        finalStatus = data.sr_prev_status;
      }

      try {
        await onUpdateOpportunity(finalStatus, '', null, '', isUndo);
      } catch (err) {
        console.error('Failed to update opportunity status:', err);
      } finally {
        this.setState({ isSubmitting: false });
      }
    }
    this.handleCloseConfirm();
  }

  renderStatusBannerHeader = (status) => {
    const { t } = this.props;
    if (this.isCancelled(status)) {
      return (
        <div className="cancel-header-title text-success">
          <strong>{t('Cancellation approved')}</strong>
        </div>
      );
    }
    return (
      <div className="cancel-header-title text-warning">
        <strong>{t('Cancellation request received')}</strong>
      </div>
    );
  }

  render() {
    const { t, data, hasAccess } = this.props;
    const { isConfirmOpen, confirmType, isSubmitting } = this.state;
    const currentStatus = data && data.sr_status;
    const isApprove = confirmType === OPPORTUNITY_STATUS.CANCELLED;

    const requestorName = (data && data.primary_customer && data.primary_customer.name) || '-';

    return (
      <div className="cancel-detail-wrapper">
        <Segment className={`cancel-status-block ${this.getBannerClassName(currentStatus)}`}>

          <div className="cancel-banner-top-row">
            {this.renderStatusBannerHeader(currentStatus)}

            {/* Action Buttons: Displayed only for HQ users while status is Pending Cancellation */}
            {(currentStatus === OPPORTUNITY_STATUS.PENDING_CANCELLATION && hasAccess) && (
              <div className="cancel-actions-header-group">
                <Button
                  className="btn-cancel-action btn-approve-cancel"
                  onClick={() => this.handleOpenConfirm(OPPORTUNITY_STATUS.CANCELLED)}
                >
                  <Icon name="checkmark" /> {t('Approve Cancellation')}
                </Button>
                <Button
                  className="btn-cancel-action btn-undo-cancel"
                  onClick={() => this.handleOpenConfirm('UNDO_CANCELLATION')}
                >
                  <Icon name="undo" /> {t('Undo Cancellation')}
                </Button>
              </div>
          )}
          </div>

          <Grid style={{ marginTop: '1rem' }} columns="equal" className="cancel-fields-grid">
            <Grid.Row className="field-row">
              <Grid.Column width={4} className="field-label">{t('Requestor name')}</Grid.Column>
              <Grid.Column width={12} className="field-value text-green font-weight-bold">
                {requestorName}
              </Grid.Column>
            </Grid.Row>

            {/* CPI-1226: the scenario sits ABOVE the reason, matching the order of the two
                fields in the cancel modal. '-' for the cancellations recorded before the
                scenario existed. */}
            <Grid.Row className="field-row">
              <Grid.Column width={4} className="field-label">{t('Cancellation category')}</Grid.Column>
              <Grid.Column width={12} className="field-value">
                {cancellationCategoryLabel(data.sr_cancellation_type) ?
                  t(cancellationCategoryLabel(data.sr_cancellation_type)) : '-'}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className="field-row">
              <Grid.Column width={4} className="field-label">{t('Cancellation reason')}</Grid.Column>
              <Grid.Column width={12} className="field-value">
                {data.sr_cancellation_reason || '-'}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className="field-row">
              <Grid.Column width={4} className="field-label">{t('Cancellation requested at')}</Grid.Column>
              <Grid.Column width={12} className="field-value">
                {data.sr_cancellation_at ? <Timestamp timestamp={data.sr_cancellation_at} format="DD MMM YYYY, hh:mm a" /> : '-'}
              </Grid.Column>
            </Grid.Row>

            {this.isCancelled(currentStatus) && (
            <Grid.Row className="field-row">
              <Grid.Column width={4} className="field-label">{t('Cancellation approved at')}</Grid.Column>
              <Grid.Column width={12} className="field-value">
                {data.sr_cancelled_at ? <Timestamp timestamp={data.sr_cancelled_at} format="DD MMM YYYY, hh:mm a" /> : '-'}
              </Grid.Column>
            </Grid.Row>
          )}

          </Grid>
        </Segment>

        <Modal
          open={isConfirmOpen}
          onClose={this.handleCloseConfirm}
          size={isApprove ? 'mini' : 'tiny'}
          closeIcon
        >
          <Modal.Header>{isApprove ? t('Approve Cancellation') : t('Undo Cancellation')}</Modal.Header>
          <Modal.Content>
            {isApprove ? (
              <div>
                <p className="confirm-approve-text">
                  {t('Are you sure you want to approve this cancellation request')}
                </p>
              </div>
            ) : (
              <div className="reject-refund-wrapper">
                <p className="confirm-reject-text">
                  {t('Are you sure you want to undo this cancellation request')}
                </p>
              </div>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button basic onClick={this.handleCloseConfirm}>{t('Cancel')}</Button>
            <Button
              color="teal"
              onClick={this.handleConfirmAction}
              loading={isSubmitting}
            >
              {t('Confirm')}
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
    );
  }
}

export default withTranslation()(CancelRequestDetail);



// WEBPACK FOOTER //
// ./src/components/CancelRequestDetail.jsx